import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  Signal,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { Paginator } from 'primeng/paginator';
import { RouterLink } from '@angular/router';
import { ConversionData, MbrsStatusEnum } from '../../../../types/commons';
import { RetrieveMbrsDataService } from '../../../services/mbrs/retrieve-mbrs-data.service';
import { ConversionListingService } from '../../../services/common/conversion-listing.service';
import { DEI, DEI_EE_MFRS } from '../../../constants/qnames';
import { TruncatePipe } from '../../../utils/pipes/truncate.pipe';

/**
 * Narrow read surface the debug layout needs from the XBRL Files host.
 * Keeps the host free to evolve its private API without dragging this component along.
 */
export interface XbrlFilesDebugHost {
  fullData: Signal<ConversionData[]>;
  totalRecords: Signal<number>;
  isTableLoading: Signal<boolean>;
  first: Signal<number>;
  rows: Signal<number>;
  MbrsStatusEnum: typeof MbrsStatusEnum;
  conversionListingService: ConversionListingService;

  onPageChange(event: { first?: number; rows?: number }): void;
  onSearchChange(query: string): void;
  onStatusChange(): void;
  clearFilters(): void;
  downloadXbrlFile(item: ConversionData): void;
  deleteXbrlFile(item: ConversionData): void;
  min(first: number, second: number): number;
}

/** Key filing-info qnames we surface as filterable columns. */
const FILING_INFO_FIELDS: { qname: string; label: string; shortLabel: string }[] = [
  { qname: DEI_EE_MFRS.NatureOfFinancialStatements, label: 'Nature of financial statements', shortLabel: 'Nature' },
  { qname: DEI.StatusOfCompany, label: 'Status of company', shortLabel: 'Company Status' },
  { qname: DEI.TypeOfCompany, label: 'Types of company', shortLabel: 'Company Type' },
  { qname: DEI.BasisOfAccountingStandardsAppliedToPrepareFinancialStatements, label: 'Accounting standards', shortLabel: 'Acct. Standards' },
  { qname: DEI.DisclosureOfFinancialStatementsAuditStatus, label: 'Audit status', shortLabel: 'Audit Status' },
  { qname: DEI.TypeOfSubmission, label: 'Type of submission', shortLabel: 'Submission Type' },
  { qname: DEI.DescriptionOfPresentationCurrency, label: 'Presentation currency', shortLabel: 'Currency' },
  { qname: DEI.LevelOfRoundingUsedInFinancialStatements, label: 'Level of rounding', shortLabel: 'Rounding' },
  { qname: DEI.CompanyCurrentFinancialYearStartDate, label: 'FY start date', shortLabel: 'FY Start' },
  { qname: DEI.CompanyCurrentFinancialYearEndDate, label: 'FY end date', shortLabel: 'FY End' },
];

/** Cached filing info for a single conversion. */
interface FilingInfoEntry {
  data: Record<string, string | unknown>;
  loadedAt: number;
}

/**
 * XBRL Files — Developer Debug Layout.
 *
 * This is a **non-production, opt-in** alternative view of the XBRL Files
 * listing page. It augments the standard table with:
 *   - Expandable rows that lazy-load filing info via `fetchMbrsData`
 *   - Client-side filters on filing-info fields (Nature, Company Status, etc.)
 *   - A quick-inspect panel showing the raw MbrsSheetData as JSON
 *   - Bulk-load button to fetch filing info for all visible conversions
 *
 * Toggled via Ctrl+Alt+D (same as Role 19 debug layout). Only available
 * when `environment.production === false`.
 */
@Component({
  selector: 'app-xbrl-files-debug-layout',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    SelectModule,
    InputTextModule,
    SkeletonModule,
    TooltipModule,
    TagModule,
    Paginator,
    TruncatePipe,
    RouterLink,
  ],
  templateUrl: './xbrl-files-debug-layout.component.html',
  styleUrls: ['./xbrl-files-debug-layout.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XbrlFilesDebugLayoutComponent implements AfterViewInit {
  @Input({ required: true }) host!: XbrlFilesDebugHost;

  private readonly retrieveMbrsDataService = inject(RetrieveMbrsDataService);
  private readonly destroyRef = inject(DestroyRef);

  readonly filingInfoFields = FILING_INFO_FIELDS;
  readonly MbrsStatusEnum = MbrsStatusEnum;
  // Exposed for template usage so the HTML does not re-introduce qname literals.
  readonly DEI = DEI;
  readonly DEI_EE_MFRS = DEI_EE_MFRS;

  // ─── Filing info cache ──────────────────────────────────────────
  /** Immutable signal map: conversionId -> FilingInfoEntry. */
  private readonly filingInfoCache = signal<Map<string, FilingInfoEntry>>(new Map());
  /** Set of conversionIds currently being fetched. */
  private readonly loadingIds = signal<Set<string>>(new Set());

  // ─── View state ─────────────────────────────────────────────────
  readonly expandedRowIds = signal<Set<string>>(new Set());
  readonly inspectedConversionId = signal<string | null>(null);
  readonly bulkLoadProgress = signal<{ loaded: number; total: number } | null>(null);

  // ─── Debug filters (client-side, on top of server-side pagination) ─
  readonly natureFilter = signal<string>('');
  readonly companyStatusFilter = signal<string>('');
  readonly accountingStdFilter = signal<string>('');
  readonly auditStatusFilter = signal<string>('');
  readonly submissionTypeFilter = signal<string>('');
  readonly statusFilter = signal<string>('');
  readonly companyFileSearchQuery = signal<string>('');

  constructor() {
    // Auto-load filing info whenever the host's data changes (page change, initial load, etc.)
    effect(() => {
      const data = this.host.fullData();
      if (data.length > 0) {
        // Use setTimeout to avoid triggering during change detection
        setTimeout(() => this.bulkLoadAll());
      }
    });
  }

  ngAfterViewInit(): void {
    this.destroyRef.onDestroy(() => {
      if (this.toastTimeout) clearTimeout(this.toastTimeout);
    });
  }

  // ─── Filter option sets (built from cached filing info) ─────────
  readonly natureOptions = computed(() => this.buildFilterOptions(DEI_EE_MFRS.NatureOfFinancialStatements));
  readonly companyStatusOptions = computed(() => this.buildFilterOptions(DEI.StatusOfCompany));
  readonly accountingStdOptions = computed(() => this.buildFilterOptions(DEI.BasisOfAccountingStandardsAppliedToPrepareFinancialStatements));
  readonly auditStatusOptions = computed(() => this.buildFilterOptions(DEI.DisclosureOfFinancialStatementsAuditStatus));
  readonly submissionTypeOptions = computed(() => this.buildFilterOptions(DEI.TypeOfSubmission));

  readonly statusOptions: { label: string; value: string }[] = [
    { label: 'All', value: '' },
    { label: 'In Progress', value: String(MbrsStatusEnum.InProgress) },
    { label: 'Review', value: String(MbrsStatusEnum.Review) },
    { label: 'Success', value: String(MbrsStatusEnum.Success) },
    { label: 'Failed', value: String(MbrsStatusEnum.Failed) },
  ];

  /** Build unique option values from cached filing info for a given qname. */
  private buildFilterOptions(qname: string): { label: string; value: string }[] {
    const cache = this.filingInfoCache();
    const values = new Set<string>();
    for (const entry of cache.values()) {
      const val = entry.data[qname];
      if (typeof val === 'string' && val.trim()) values.add(val.trim());
    }
    const sorted = Array.from(values).sort();
    return [{ label: 'All', value: '' }, ...sorted.map((option) => ({ label: option, value: option }))];
  }

  /** Conversions filtered by debug filters (client-side). */
  readonly filteredConversions = computed<ConversionData[]>(() => {
    const all = this.host.fullData();
    const cache = this.filingInfoCache();
    const nature = this.natureFilter();
    const companyStatus = this.companyStatusFilter();
    const acctStd = this.accountingStdFilter();
    const audit = this.auditStatusFilter();
    const submission = this.submissionTypeFilter();
    const conversionStatus = this.statusFilter();
    const search = this.companyFileSearchQuery().trim().toLowerCase();

    const hasFilingInfoFilter = nature || companyStatus || acctStd || audit || submission;

    return all.filter((conv) => {
      // Conversion status filter (operates on ConversionData directly)
      if (conversionStatus && String(conv.status) !== conversionStatus) return false;

      // Text search applies to company name and filename
      if (search) {
        const companyMatch = conv.company?.toLowerCase().includes(search) ?? false;
        const fileMatch = conv.fileName?.toLowerCase().includes(search) ?? false;
        if (!companyMatch && !fileMatch) return false;
      }

      if (!hasFilingInfoFilter) return true;

      const entry = cache.get(conv.id);
      if (!entry) return false;

      const filingFields = entry.data;
      if (nature && this.fieldValue(filingFields, DEI_EE_MFRS.NatureOfFinancialStatements) !== nature) return false;
      if (companyStatus && this.fieldValue(filingFields, DEI.StatusOfCompany) !== companyStatus) return false;
      if (acctStd && this.fieldValue(filingFields, DEI.BasisOfAccountingStandardsAppliedToPrepareFinancialStatements) !== acctStd) return false;
      if (audit && this.fieldValue(filingFields, DEI.DisclosureOfFinancialStatementsAuditStatus) !== audit) return false;
      if (submission && this.fieldValue(filingFields, DEI.TypeOfSubmission) !== submission) return false;

      return true;
    });
  });

  /** Summary counts for the debug banner. */
  readonly summary = computed(() => {
    const all = this.host.fullData();
    let inProgress = 0;
    let review = 0;
    let success = 0;
    let failed = 0;
    for (const conversion of all) {
      switch (conversion.status) {
        case MbrsStatusEnum.InProgress: inProgress++; break;
        case MbrsStatusEnum.Review:     review++;     break;
        case MbrsStatusEnum.Success:    success++;    break;
        case MbrsStatusEnum.Failed:     failed++;     break;
      }
    }
    return { total: all.length, inProgress, review, success, failed };
  });

  // ─── Cache helpers ──────────────────────────────────────────────
  isLoading(conversionId: string): boolean {
    return this.loadingIds().has(conversionId);
  }

  hasFilingInfo(conversionId: string): boolean {
    return this.filingInfoCache().has(conversionId);
  }

  getFilingInfo(conversionId: string): Record<string, string | unknown> | null {
    return this.filingInfoCache().get(conversionId)?.data ?? null;
  }

  getFilingInfoField(conversionId: string, qname: string): string {
    const entry = this.filingInfoCache().get(conversionId);
    if (!entry) return '';
    return this.fieldValue(entry.data, qname);
  }

  /** Inspected conversion's full sheet data as formatted JSON. */
  readonly inspectedJson = computed<string>(() => {
    const id = this.inspectedConversionId();
    if (!id) return '';
    const entry = this.filingInfoCache().get(id);
    if (!entry) return '(not loaded)';
    return JSON.stringify(entry.data, null, 2);
  });

  // ─── Row expansion + lazy load ──────────────────────────────────
  toggleRow(conversionId: string): void {
    const expanded = new Set(this.expandedRowIds());
    if (expanded.has(conversionId)) {
      expanded.delete(conversionId);
    } else {
      expanded.add(conversionId);
      if (!this.hasFilingInfo(conversionId) && !this.isLoading(conversionId)) {
        this.loadFilingInfo(conversionId);
      }
    }
    this.expandedRowIds.set(expanded);
  }

  isExpanded(conversionId: string): boolean {
    return this.expandedRowIds().has(conversionId);
  }

  loadFilingInfo(conversionId: string): void {
    if (this.isLoading(conversionId) || this.hasFilingInfo(conversionId)) return;

    this.loadingIds.update((currentIds) => new Set([...currentIds, conversionId]));

    this.retrieveMbrsDataService
      .fetchSheetDataRaw(conversionId, '')
      .pipe(
        catchError(() => of({} as Record<string, string | unknown>)),
        finalize(() => {
          this.loadingIds.update((currentIds) => {
            const next = new Set(currentIds);
            next.delete(conversionId);
            return next;
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((filingData) => {
        this.filingInfoCache.update((cache) => {
          const next = new Map(cache);
          next.set(conversionId, { data: filingData, loadedAt: Date.now() });
          return next;
        });
      });
  }

  // ─── Bulk load / reload ─────────────────────────────────────────
  /** Clears cached filing info and re-fetches all visible conversions. */
  reloadAll(): void {
    this.filingInfoCache.set(new Map());
    this.bulkLoadAll();
  }

  bulkLoadAll(): void {
    const all = this.host.fullData();
    const uncached = all.filter(
      (conversion) => !this.hasFilingInfo(conversion.id) && !this.isLoading(conversion.id) && conversion.status !== MbrsStatusEnum.InProgress,
    );
    if (uncached.length === 0) return;

    this.bulkLoadProgress.set({ loaded: 0, total: uncached.length });

    // Fire individual requests (max ~4 concurrent via browser limits)
    const observables = uncached.map((conversion) =>
      this.retrieveMbrsDataService.fetchSheetDataRaw(conversion.id, '').pipe(
        catchError(() => of({} as Record<string, string | unknown>)),
      ),
    );

    let completed = 0;
    forkJoin(
      observables.map((obs$) =>
        obs$.pipe(
          finalize(() => {
            completed++;
            this.bulkLoadProgress.set({ loaded: completed, total: uncached.length });
          }),
        ),
      ),
    )
      .pipe(
        finalize(() => {
          setTimeout(() => this.bulkLoadProgress.set(null), 1500);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((results) => {
        this.filingInfoCache.update((cache) => {
          const next = new Map(cache);
          results.forEach((filingData, resultIndex) => {
            const convId = uncached[resultIndex].id;
            next.set(convId, { data: filingData, loadedAt: Date.now() });
          });
          return next;
        });
      });
  }

  // ─── Inspect panel ──────────────────────────────────────────────
  openInspect(conversionId: string): void {
    if (!this.hasFilingInfo(conversionId)) {
      this.loadFilingInfo(conversionId);
    }
    this.inspectedConversionId.set(conversionId);
  }

  closeInspect(): void {
    this.inspectedConversionId.set(null);
  }

  copyInspectJson(): void {
    const json = this.inspectedJson();
    if (!json || json === '(not loaded)') return;
    navigator.clipboard?.writeText(json).then(
      () => this.flashMessage('JSON copied to clipboard.'),
      () => this.flashMessage('Clipboard write failed.', true),
    );
  }

  // ─── Filter helpers ─────────────────────────────────────────────
  clearDebugFilters(): void {
    this.natureFilter.set('');
    this.companyStatusFilter.set('');
    this.accountingStdFilter.set('');
    this.auditStatusFilter.set('');
    this.submissionTypeFilter.set('');
    this.statusFilter.set('');
    this.companyFileSearchQuery.set('');
  }

  hasActiveDebugFilters(): boolean {
    return !!(
      this.natureFilter() ||
      this.companyStatusFilter() ||
      this.accountingStdFilter() ||
      this.auditStatusFilter() ||
      this.submissionTypeFilter() ||
      this.statusFilter() ||
      this.companyFileSearchQuery()
    );
  }

  // ─── Toast-lite ─────────────────────────────────────────────────
  readonly toastMessage = signal<{ text: string; error: boolean } | null>(null);
  private toastTimeout: ReturnType<typeof setTimeout> | null = null;

  private flashMessage(text: string, error = false): void {
    this.toastMessage.set({ text, error });
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    this.toastTimeout = setTimeout(() => this.toastMessage.set(null), 2200);
  }

  // ─── Utilities ──────────────────────────────────────────────────
  private fieldValue(data: Record<string, string | unknown>, qname: string): string {
    const val = data[qname];
    return typeof val === 'string' ? val.trim() : '';
  }

  trackById(_unusedIndex: number, item: ConversionData): string {
    return item.id;
  }
}
